import { css, keyframes } from '@emotion/css';
import { useEffect } from 'react';
import { useClickOutside } from '../../../shared/custom-hooks/custom-hooks';
import { Colors } from '../../../shared/styles/colors';
import { Radius, Shadow } from '../../../shared/styles/tokens';

interface IModalProps {
  visible: boolean;
  setVisible: (arg: boolean) => void;
  children: JSX.Element;
}

export const Modal = (props: IModalProps): JSX.Element => {
  const { children, setVisible, visible } = props;
  const modalRef = useClickOutside(() => {
    setVisible(false);
  }) as React.MutableRefObject<HTMLDivElement>;

  // Escape closes the dialog like the backdrop click does.
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, setVisible]);

  return (
    <div className={modalStyle(visible)} role="presentation">
      <section
        className={modalContentStyle}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </section>
    </div>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.96); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const modalStyle = (visible: boolean) => css`
  display: ${visible ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 200;
  background: rgba(18, 58, 87, 0.45);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 180ms ease-out;
`;

const modalContentStyle = css`
  display: flex;
  position: fixed;
  background: ${Colors.WHITE};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 1.5rem 1.5rem 0px 1.5rem;
  border-radius: ${Radius.xl};
  box-shadow: ${Shadow.raised};
  width: min(90%, 26rem);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  animation: ${popIn} 220ms cubic-bezier(0.2, 0, 0, 1);
`;
